import type {
  DoseComponentFormValues,
  DoseFormValues,
  DoseLocationFormValues,
} from "@/components/dose/dose-form/dose-form-schema";

import { getDoseComponents, isDoseCheckedOut } from "@/lib/dose-components";

import type { DoseComponent, MedicationSummary } from "@/types/medication";

// Floating point would leave 20 - 7.1 as 12.899999999999999.
const round = (value: number) => Math.round(value * 1e6) / 1e6;

/** Index of the component a site was drawn from; a site without one drew from the first. */
export function siteComponentIndex(
  components: { id?: string }[],
  componentId: string | null | undefined,
): number {
  return Math.max(
    0,
    componentId
      ? components.findIndex((component) => component.id === componentId)
      : -1,
  );
}

/**
 * Whether a site's lot and reconstitution can be set apart from the rest of the
 * dose. A site sharing its component with others takes its own amount out of it,
 * so it needs an amount, and something must be left for the others.
 */
export function canEditSiteComponent(
  dose: MedicationSummary,
  siteIndex: number,
): boolean {
  const components: DoseComponent[] = getDoseComponents(dose);
  const sites = dose.locations ?? [];
  const site = sites[siteIndex];
  if (!site || isDoseCheckedOut(dose)) return false;
  const index = siteComponentIndex(components, site.doseComponentId);
  const others = sites.filter(
    (other, otherIndex) =>
      otherIndex !== siteIndex &&
      siteComponentIndex(components, other.doseComponentId) === index,
  );
  if (others.length === 0) return true;
  const amount = site.amount ?? 0;
  if (amount <= 0) return false;
  if (
    (components[index]?.quantity ?? 1) === 1 &&
    others.every((other) => other.amount != null)
  ) {
    return others.some((other) => (other.amount ?? 0) > 0);
  }
  return amount < (components[index]?.amount ?? 0);
}

const hasAmount = (row: DoseLocationFormValues) => row.amount.trim() !== "";

const sumAmounts = (rows: DoseLocationFormValues[]) =>
  round(rows.reduce((sum, row) => sum + Number(row.amount), 0));

const lotKey = (component: DoseComponentFormValues) =>
  JSON.stringify([
    (component.lotSelection?.value ?? "").trim().toLowerCase(),
    (component.lotAllocations ?? [])
      .map((allocation) => allocation.shipmentId)
      .sort(),
  ]);

const drawnAlike = (a: DoseComponentFormValues, b: DoseComponentFormValues) =>
  a.medicationId === b.medicationId &&
  a.unit === b.unit &&
  (a.dilutionId ?? null) === (b.dilutionId ?? null) &&
  lotKey(a) === lotKey(b);

/**
 * Changes the component one site was drawn from. A site sharing it with others
 * gets a component of its own carrying its amount, so stock, expiry and billing
 * follow each lot; a site that ends up drawn like another component joins it.
 */
export function editSiteComponent(
  values: DoseFormValues,
  siteIndex: number,
  change: (component: DoseComponentFormValues) => DoseComponentFormValues,
): DoseFormValues {
  const rows: DoseLocationFormValues[] = values.locations ?? [];
  const components = [...values.components];
  const drawnFrom = rows.map(
    (row) =>
      components[
        row.componentIndex ??
          siteComponentIndex(components, row.componentId ?? null)
      ],
  );
  const swap = (
    from: DoseComponentFormValues,
    to: DoseComponentFormValues | null,
  ) => {
    const index = components.indexOf(from);
    if (to) components[index] = to;
    else components.splice(index, 1);
    drawnFrom.forEach((component, row) => {
      if (component === from && to) drawnFrom[row] = to;
    });
  };

  const current = drawnFrom[siteIndex];
  const shared = drawnFrom.filter((component) => component === current).length;
  let next: DoseComponentFormValues;
  if (shared > 1) {
    const amount = Number(rows[siteIndex].amount);
    const others = rows.filter(
      (_, row) => row !== siteIndex && drawnFrom[row] === current,
    );
    // The part keeps what the other sites add up to; without their amounts, what
    // is left of its own.
    const remaining =
      (current.quantity ?? 1) === 1 && others.every(hasAmount)
        ? sumAmounts(others)
        : round(current.amount - amount);
    swap(current, { ...current, amount: remaining });
    // A new part has drawn nothing yet, so it starts without the original's
    // recorded stock.
    next = change({
      ...current,
      id: undefined,
      amount,
      inventoryBaseline: undefined,
    });
    components.push(next);
  } else {
    next = change(current);
    swap(current, next);
  }
  drawnFrom[siteIndex] = next;

  const twin = components.find(
    (component) => component !== next && drawnAlike(component, next),
  );
  if (twin) {
    const merged = { ...twin, amount: round(twin.amount + next.amount) };
    swap(twin, merged);
    drawnFrom.forEach((component, row) => {
      if (component === next) drawnFrom[row] = merged;
    });
    swap(next, null);
  }

  return {
    ...values,
    components,
    locations: rows.map((row, index) => ({
      ...row,
      componentIndex: components.indexOf(drawnFrom[index]),
    })),
  };
}

/**
 * A site's new amount. On a dose of one medication, the part the site draws from
 * becomes the sum of its sites, so the dose's total follows them; a part with a
 * site left blank keeps its amount, and a sold dose keeps its parts as sold.
 */
export function editSiteAmount(
  values: DoseFormValues,
  siteIndex: number,
  amount: number,
  { adjustsPart }: { adjustsPart: boolean },
): DoseFormValues {
  const rows = values.locations ?? [];
  const locations = rows.map((current, index) =>
    index === siteIndex ? { ...current, amount: String(amount) } : current,
  );
  if (!adjustsPart || !rows[siteIndex]) return { ...values, locations };
  const partOf = (row: DoseLocationFormValues) =>
    row.componentIndex ??
    siteComponentIndex(values.components, row.componentId ?? null);
  const componentIndex = partOf(rows[siteIndex]);
  const sites = locations.filter((row) => partOf(row) === componentIndex);
  // Sites record what went in, not per-vial amounts, so a part counted in
  // several vials keeps its own.
  const quantity = values.components[componentIndex]?.quantity ?? 1;
  if (quantity !== 1 || !sites.every(hasAmount)) {
    return { ...values, locations };
  }
  const total = sumAmounts(sites);
  if (total <= 0) return { ...values, locations };
  return {
    ...values,
    locations,
    components: values.components.map((current, index) =>
      index === componentIndex ? { ...current, amount: total } : current,
    ),
  };
}
